import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';
import HeaderContentLayout from '../layouts/headerContent';
import { useSelector, useDispatch } from 'react-redux';
import { CampaignSelectors } from '@copilot/common/store/selectors/campaign';
import CampaignDashboardHeader from './header';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { CampaignType } from '@copilot/data/responses/interface';
import CampaignDashboardContent from './campaignDashboard';
import { CampaignStatusEnum } from '@copilot/data/requests/models';
import { useNavigate, useParams, useLocation } from 'react-router';
import { loadCampaignMembersAction } from '@copilot/common/utils/campaignMember/saga';
import { AppLoader } from '@copilot/teams/src/components/app-loader/appLoader';
import { isNil } from 'lodash';
/**
 * Browser state type for data cached for redirects
 * Note: need separate states for different tab types to avoid redirect behaviour happening on the wrong tab
 * @param {string} usageLimitsDrawer the type of usageLimitsDrawer that must be opened (nurture/prospecting)
 * @param {string} teamMembersRedirectMemberId id of the campaign member upon redirect to the team members tab (undefined for no redirect / no member selected)
 * @param {string} searchListRedirectMemberId id of the campaign member upon redirect to the search list / nurture list tab (undefined for no redirect / no member selected)
 */

export function OldCampaignDetails() {
    const { campaignId = '' } = useParams();
    const campaign = useSelector(CampaignSelectors.getCampaign(campaignId));
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);

    const location = useLocation();
    const [teamMembersRedirectMemberId, setTeamMembersRedirectMemberId] = useState<string>();
    const [searchListRedirectMemberId, setSearchListRedirectMemberId] = useState<string>();
    const clearRedirectedMemberIds = () => {
        setTeamMembersRedirectMemberId(undefined);
        setSearchListRedirectMemberId(undefined);
    };

    const navigate = useNavigate();
    const storeDispatch = useDispatch();

    useEffect(() => {
        if (!isNil(campaign)) {
            storeDispatch(loadCampaignMembersAction(campaign.id));
        }
    }, [campaign?.id]);

    useEffect(() => {
        const teamMembersRedirectMemberIdParam: string | undefined =
            location.state?.teamMembersRedirectMemberId;
        const searchListRedirectMemberIdParam: string | undefined =
            location.state?.searchListRedirectMemberId;

        if (!isNil(teamMembersRedirectMemberId) || !isNil(searchListRedirectMemberId)) {
            setTeamMembersRedirectMemberId(teamMembersRedirectMemberIdParam);
            setSearchListRedirectMemberId(searchListRedirectMemberIdParam);
            // state const is required, because its value cannot be written directly in history.replace
            const state = {
                ...location.state,
                teamMembersRedirectMemberId: undefined,
                searchListRedirectMemberId: undefined,
            };
            // used different history.replace from usageLimitsDrawer useEffect because specifying path breaks redirect functionality
            //		(redirects to `Summary` tab instead of `Search/Nurture List`)
            navigate({ ...location }, { state });
        }
    }, [location, navigate]);

    const dashboardContent = useMemo(() => {
        if (isNil(campaign)) {
            return null;
        }

        switch (campaign.type) {
            case CampaignType.Prospecting: {
                return (
                    <CampaignDashboardContent
                        campaign={campaign}
                        isTeamUser={true}
                        key={campaign.id}
                        teamMembersRedirectMemberId={teamMembersRedirectMemberId}
                        searchListRedirectMemberId={searchListRedirectMemberId}
                        clearRedirectedMemberIds={clearRedirectedMemberIds}
                    />
                );
            }
            case CampaignType.Nurture: {
                return (
                    <CampaignDashboardContent
                        campaign={campaign}
                        isTeamUser={true}
                        key={campaign.id}
                        teamMembersRedirectMemberId={teamMembersRedirectMemberId}
                        searchListRedirectMemberId={searchListRedirectMemberId}
                        clearRedirectedMemberIds={clearRedirectedMemberIds}
                    />
                );
            }
            default:
                throw new Error(`CampaignType: ${campaign.type} not handled`);
        }
    }, [
        campaign,
        activeMember?.isOrgAdmin,
        teamMembersRedirectMemberId,
        searchListRedirectMemberId,
    ]);

    if (isNil(campaign)) {
        return <AppLoader />;
    }

    return (
        <HeaderContentLayout>
            {campaign.status === CampaignStatusEnum.Disabled && (
                <Alert message="Campaign is disabled" type="warning" showIcon />
            )}
            <HeaderContentLayout.Header>
                <CampaignDashboardHeader campaign={campaign} />
            </HeaderContentLayout.Header>
            {dashboardContent}
        </HeaderContentLayout>
    );
}
